import {UsuarioInformacao} from "@components/contas/UsuarioInformacao.tsx";
import {useMovimentacaoRecurso} from "@components/recurso/hooks/useMovimentacaoRecurso.ts";
import {RecursoInfoTextField} from "@components/recurso/RecursoInformacao.tsx";
import {MoneyInputEntry} from "@components/shared/FormEntries/InputEntry.tsx";
import {useApi} from "@contexts/hooks/api/useApi.ts";
import {useAlert} from "@contexts/hooks/useAlert.ts";
import {useContextMenu} from "@contexts/hooks/useContextMenu.ts";
import {_RecursosEndpoints} from "@localization/siteMap/endpoints/_RecursosEndpoints.ts";
import {DefaultEventProps, DefaultInfoV1Props} from "@models/DefaultProps.ts";
import {IRecursoMovimentacao} from "@models/recurso/IRecursoMovimentacao.ts";
import {Cancel, OpenInNew, ThumbUp} from "@mui/icons-material";
import {Button, ButtonGroup, Grid, IconButton, Paper, TableCell, TableRow, TextField, Typography} from "@mui/material";
import {ToDateDefault, ToMoney} from "@shared/MathHelp.ts";
import React, {useEffect, useState} from "react";
import {createSearchParams, useNavigate} from "react-router";
import {IRecurso} from "@models/recurso/IRecurso.ts";

interface RecursoMovimentacaoInformacaoProps extends DefaultInfoV1Props<IRecursoMovimentacao> {
    variant: 'grid' | 'list' | 'row';
    removeOpenButton?: boolean;
}

export const RecursoMovimentacaoInformacao: React.FC<RecursoMovimentacaoInformacaoProps> = (props) => {
    return (<RecursoMovimentacaoInfoList {...props}/>)
}

export const RecursoMovimentacaoInfoList: React.FC<RecursoMovimentacaoInformacaoProps> = (props) => {
    const navigate = useNavigate()
    const {removeOpenButton} = props
    const [value, setValue] = useState<IRecursoMovimentacao | undefined>(props.value)

    useEffect(() => {
        if (value != null) {
            setValue(props.value)
        }
    }, [props.value])


    return (value &&
        <Grid container>
            <Grid size={12} component={Paper} elevation={1} p={1}>
                <Grid container spacing={1} justifyContent={'space-between'}>
                    <Grid container spacing={1} alignContent={'center'}>
                        <Grid>
                            <Typography>{value.id} {ToDateDefault(value.dataMovimentacao)}</Typography>
                        </Grid>
                        <Grid>
                            <Typography>{ToMoney(value.valorEfetivo)}(pendente: {ToMoney(value.valorPendente)})</Typography>
                        </Grid>
                        <Grid>
                            <Typography>{value.motivo}</Typography>
                        </Grid>
                    </Grid>
                    {!removeOpenButton && value.orcamentoId && <Grid>
                        <IconButton size={'small'} onClick={() => {
                            if (value.orcamentoId) navigate({
                                pathname: '/orcamento/detalhes',
                                search: createSearchParams({
                                    orcamentoId: value.orcamentoId.toString()
                                }).toString()
                            })
                        }}
                        >
                            <OpenInNew/>
                        </IconButton>
                    </Grid>}
                </Grid>
            </Grid>
        </Grid>
    );
}

export function RecursoMovimentacaoInfoRow(props: Omit<RecursoMovimentacaoInformacaoProps, 'removeOpenButton' | 'variant'>) {
    const {EndComponent} = props
    const {setContextOptions} = useContextMenu()
    const {getMovimentacaoRecursoById} = useMovimentacaoRecurso()
    const [value, setValue] = useState<IRecursoMovimentacao | undefined>(props.value)

    const navigate = useNavigate()

    useEffect(() => {
        if (value == null && props.expectedId != null) {
            getMovimentacaoRecursoById(props.expectedId).then(x => {
                setValue(x)
            })
        }
    }, [value]);

    const handleCtx = (e: React.MouseEvent<HTMLElement>) => {
        if (value == null) return
        setContextOptions({
            options: [{
                label: 'Visualizar',
                onClick: () => {
                    navigate({
                        pathname: '/recurso/movimentacao/detalhes',
                        search: createSearchParams({
                            movimentacaoId: value.id.toString()
                        }).toString()
                    })
                }
            }],
            anchor: e.currentTarget
        })
    }


    return (value &&
        <TableRow hover={true}>
            <TableCell onContextMenu={handleCtx}>{value.id}</TableCell>
            <TableCell onContextMenu={handleCtx}>{ToMoney(value.valorEfetivo)}</TableCell>
            <TableCell onContextMenu={handleCtx}>{ToMoney(value.valorPendente)}</TableCell>
            <TableCell onContextMenu={handleCtx}>{ToMoney(value.valorCancelado)}</TableCell>
            <TableCell onContextMenu={handleCtx}>{value.motivo}</TableCell>
            <TableCell onContextMenu={handleCtx}>{ToDateDefault(value.dataMovimentacao)}</TableCell>
            <TableCell onContextMenu={handleCtx}>{tipo(value)}</TableCell>
            <TableCell onContextMenu={handleCtx}>{EndComponent && <EndComponent {...value} />}</TableCell>
        </TableRow>
    )
}

type tipos = 'Contrato' | 'Solicitacao' | 'Manual'

function tipo(value: IRecursoMovimentacao): tipos {
    if (value.contratoId) return 'Contrato'
    if (value.solicitacaoId) return 'Solicitacao'
    return 'Manual'
}

export function RecursoMovimentacaoInfoGrid(props: Omit<RecursoMovimentacaoInformacaoProps, 'removeOpenButton' | 'variant'> & Pick<DefaultEventProps<IRecursoMovimentacao>, 'onUpdate'>) {
    const {Get} = useApi()
    const {createAlertFromResponse, closePopUp} = useAlert();
    const [value, setValue] = useState<IRecursoMovimentacao | undefined>(props.value)
    const [recurso, setRecurso] = useState<IRecurso | undefined>(props.value?.recurso)

    useEffect(() => {
        if (value != null) {
            setValue(props.value)
        }
    }, [props.value])

    useEffect(() => {
        if (value?.recurso) {
            setRecurso(value.recurso);
            return;
        }

        if (value?.recursoId) {
            Get(_RecursosEndpoints.GetRecurso({ids: [value.recursoId]}, {page: 1, pageSize: 1})).then(x => {
                if (x.items.length > 0) {
                    setRecurso(x.items[0]);
                }
            });
        }

    }, [value?.recurso]);

    return (value &&
        <Grid container rowSpacing={1}>
            <Grid size={12}>
                {value && <RecursoInfoTextField value={recurso}/>}
            </Grid>
            <Grid size={12}>
                <TextField disabled label={'Motivo'} value={value.motivo} fullWidth/>
            </Grid>
            <Grid size={4}>
                <MoneyInputEntry value={value.valorEfetivo} label={'Valor efetivo'}/>
            </Grid>
            <Grid size={4}>
                <MoneyInputEntry value={value.valorPendente} label={'Valor pendente'}/>
            </Grid>
            <Grid size={4}>
                <MoneyInputEntry value={value.valorCancelado} label={'Valor cancelado'}/>
            </Grid>

            <Grid size={12}>
                <Grid container>
                    <Grid size={6}>
                        <UsuarioInformacao slotProps={{
                            textField: {
                                fullWidth: true,
                            }
                        }} variant={'textfield'} usuarioId={value.usuarioCriacaoId} label={'Criado por'}/>
                    </Grid>
                    <Grid size={6}>
                        <TextField disabled label={'Data de criação'}
                                   value={ToDateDefault(value.dataCriacao)}
                                   fullWidth/>
                    </Grid>
                </Grid>
            </Grid>
            {value.usuarioMovimentacaoId && <Grid size={12}>
                <Grid container>
                    <Grid size={6}>
                        <UsuarioInformacao slotProps={{
                            textField: {
                                fullWidth: true,
                            }
                        }} variant={'textfield'} usuarioId={value.usuarioMovimentacaoId}
                                           label={'Movimentado/cancelado por'}/>
                    </Grid>
                    <Grid size={6}>
                        <TextField disabled label={'Data de movimentação/cancelamento'}
                                   value={ToDateDefault(value.dataMovimentacao)}
                                   fullWidth/>
                    </Grid>
                </Grid>
            </Grid>}
            <Grid size={12}>
                <Grid container justifyContent={'space-between'} alignItems={'center'}>
                    <Grid/>
                    <Grid>
                        <ButtonGroup variant={'outlined'} disabled={value.valorPendente == 0}>
                            <Button color={'primary'}
                                    endIcon={<ThumbUp/>}
                                    onClick={() => {
                                        Get(_RecursosEndpoints.FinalizarMovimentacaoRecurso(value.id)).then(x => {
                                            createAlertFromResponse(x)
                                            if (x.status == 'Success') {
                                                closePopUp()
                                                if (props.onUpdate != null)
                                                    props.onUpdate(x.items)
                                            }
                                        })
                                    }}>Efetivar movimentação</Button>
                            <Button color={'secondary'}
                                    endIcon={<Cancel/>}
                                    onClick={() => {
                                        Get(_RecursosEndpoints.CancelarMovimentacaoRecurso(value.id)).then(x => {
                                            createAlertFromResponse(x)
                                            if (x.status == 'Success') {
                                                closePopUp()
                                                if (props.onUpdate != null)
                                                    props.onUpdate(x.items)
                                            }
                                        })
                                    }}>Cancelar movimentação</Button>
                        </ButtonGroup>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    )
}